import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Pencil } from 'lucide-react';

/**
 * Bulk-edit selected garments. Only ticked fields are applied.
 * props: open, onOpenChange, barcodes (array), onDone()
 */
export default function BulkEditDialog({ open, onOpenChange, barcodes = [], onDone }) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ product: false, category: false, price: false, cost: false });
  const [v, setV] = useState({ product: '', category: '', price: '', cost: '' });

  useEffect(() => {
    if (open) { setF({ product: false, category: false, price: false, cost: false }); setV({ product: '', category: '', price: '', cost: '' }); }
  }, [open]);

  const { data: cats = [] } = useQuery({ queryKey: ['categories'], queryFn: async () => (await api.get('/categories')).data.data, enabled: open });

  const save = async () => {
    const updates = {};
    if (f.product) updates.product = v.product.trim();
    if (f.category) updates.category = v.category.trim();
    if (f.price) updates.price = Number(v.price);
    if (f.cost) updates.cost = Number(v.cost);
    if (Object.keys(updates).length === 0) { toast.error('Tick at least one field to change.'); return; }
    if (f.product && !updates.product) { toast.error('Enter a new item name.'); return; }
    if (f.price && (!(Number(v.price) > 0))) { toast.error('Enter a valid selling price.'); return; }
    if (f.cost && !(Number(v.cost) >= 0)) { toast.error('Enter a valid purchase price.'); return; }
    setSaving(true);
    try {
      const r = await api.patch('/garments/bulk', { barcodes, updates });
      toast.success(`${r.data.data.modified} item(s) updated`);
      qc.invalidateQueries({ queryKey: ['garments'] });
      qc.invalidateQueries({ queryKey: ['stock-summary'] });
      qc.invalidateQueries({ queryKey: ['categories'] });
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const Row = ({ id, label, children }) => (
    <div className="flex items-start gap-3 rounded-xl border border-border p-3">
      <Checkbox id={id} checked={f[id]} onCheckedChange={(c) => setF((s) => ({ ...s, [id]: !!c }))} data-testid={`bulkedit-toggle-${id}`} className="mt-2.5" />
      <div className="flex-1">
        <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
        <div className={`mt-1 ${f[id] ? '' : 'pointer-events-none opacity-40'}`}>{children}</div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(x) => { if (!saving) onOpenChange(x); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-primary" /> Bulk Edit</DialogTitle>
          <DialogDescription>Editing {barcodes.length} selected item{barcodes.length === 1 ? '' : 's'}. Tick a field to change it for all of them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Row id="product" label="Item Name">
            <Input value={v.product} onChange={(e) => setV((s) => ({ ...s, product: e.target.value }))} className="h-10 rounded-lg" data-testid="bulkedit-product" placeholder="New name" />
          </Row>
          <Row id="category" label="Category (blank = Uncategorised)">
            <Input list="bulkedit-cats" value={v.category} onChange={(e) => setV((s) => ({ ...s, category: e.target.value }))} className="h-10 rounded-lg" data-testid="bulkedit-category" placeholder="Category" />
            <datalist id="bulkedit-cats">{cats.map((c) => <option key={c.name} value={c.name} />)}</datalist>
          </Row>
          <Row id="price" label="Selling Price">
            <Input inputMode="numeric" value={v.price} onChange={(e) => setV((s) => ({ ...s, price: e.target.value }))} className="h-10 rounded-lg" data-testid="bulkedit-price" placeholder="0" />
          </Row>
          <Row id="cost" label="Purchase Price (cost)">
            <Input inputMode="numeric" value={v.cost} onChange={(e) => setV((s) => ({ ...s, cost: e.target.value }))} className="h-10 rounded-lg" data-testid="bulkedit-cost" placeholder="0" />
          </Row>
        </div>
        <DialogFooter>
          <Button variant="secondary" className="rounded-lg" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="rounded-lg" onClick={save} disabled={saving} data-testid="bulkedit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
